"use client";
import { poppins } from "@/lib/fonts";
import { Menu, X } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { JSX, useEffect, useRef, useState } from "react";
import { useParams } from "next/navigation";
import SafeLink from "../SafeLink";
import { Locale } from "@/models/Locale";

export function HomeNavbar(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations("components.home_nav_bar");
  const ta = useTranslations("components.alt_text");
  const { locale } = useParams<{ locale: Locale }>();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const closeMenu = () => setIsOpen(false);

  return (
    <nav
      ref={navRef}
      className={`${poppins.className} fixed top-0 left-0 z-50 w-full bg-white shadow-sm md:bg-[#FFF8F2]/50 md:shadow-none md:backdrop-blur-md md:backdrop-saturate-150`}
    >
      <div className="flex w-full items-center justify-between px-4 py-6 md:container md:mx-auto">
        {/* Banner */}
        <a href={`/${locale}/`} className="flex items-center">
          <Image
            src="/dashboard-assets/plus_menu_logo_long.svg"
            alt={ta("plus_menu_brand")}
            width={180}
            height={51}
            className="h-9 w-auto md:h-13"
            priority
          />
        </a>

        {/* Desktop Navigation */}
        <div className="text-ht-brown hidden items-center gap-6 text-sm md:flex">
          <a href="/m/1" className="hover:text-h-500 transition-colors">
            {t("demo")}
          </a>
          <a href={`/${locale}/#features`} className="hover:text-h-500 transition-colors">
            {t("features")}
          </a>
          <a href={`/${locale}/#price`} className="hover:text-h-500 transition-colors">
            {t("price")}
          </a>
          {locale === "tr" && (
            <a href={`/${locale}/blog/`} className="hover:text-h-500 transition-colors">
              Blog
            </a>
          )}
          <a href={`/${locale}/contact/`} className="hover:text-h-500 transition-colors">
            {t("contact")}
          </a>
        </div>

        {/* Buttons */}
        <div className="hidden gap-4 text-sm font-medium md:flex">
          <SafeLink
            href="/sign-in"
            locale={locale}
            className="text-h-500 cursor-pointer rounded-4xl p-3 px-6 hover:bg-gray-100"
          >
            {t("login")}
          </SafeLink>
          <SafeLink
            href="/sign-up"
            locale={locale}
            className="bg-h-500 cursor-pointer rounded-4xl p-3 px-6 text-white hover:drop-shadow-lg"
          >
            {t("sign_up")}
          </SafeLink>
        </div>

        {/* Mobile Menu Icon */}
        <div className="md:hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center justify-center p-2 -mr-2 min-w-[44px] min-h-[44px] touch-manipulation cursor-pointer text-gray-800 focus:outline-none"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 z-50 w-full border-t border-gray-200 bg-white px-6 py-4 shadow-md md:hidden">
          <div className="text-ht-brown flex flex-col gap-4 text-sm">
            <a href="/m/1" onClick={closeMenu}>
              {t("demo")}
            </a>
            <a href={`/${locale}/#features`} onClick={closeMenu}>
              {t("features")}
            </a>
            <a href={`/${locale}/#price`} onClick={closeMenu}>
              {t("price")}
            </a>
            {locale === "tr" && (
              <a href={`/${locale}/blog/`} onClick={closeMenu}>
                Blog
              </a>
            )}
            <a href={`/${locale}/contact/`} onClick={closeMenu}>
              {t("contact")}
            </a>
            <div className="h-px w-full bg-gray-200" />
            <div className="flex w-full flex-col gap-4 sm:grid sm:grid-cols-2">
              <SafeLink
                href="/sign-in"
                locale={locale}
                onClick={closeMenu}
                className="text-h-500 border-h-500 rounded-lg border p-3 text-center hover:bg-gray-100"
              >
                {t("login")}
              </SafeLink>
              <SafeLink
                href="/sign-up"
                locale={locale}
                onClick={closeMenu}
                className="bg-h-500 rounded-lg p-3 text-center text-white hover:drop-shadow-lg"
              >
                {t("sign_up")}
              </SafeLink>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
